import EngineeringIcon from "@mui/icons-material/Engineering";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HikingIcon from "@mui/icons-material/Hiking";
import ScienceIcon from "@mui/icons-material/Science";

import { NavSection } from "../../components/LeftNavV2/LeftNavDrawerV2";
import { type GroupChildNavItem } from "../../components/LeftNavV2/NavItemV2";

function staffChild(text: string, path: string, description?: string): GroupChildNavItem {
  return {
    kind: "GroupChild",
    text,
    path,
    variant: "staff",
    description,
  };
}

export function useStaffNavSectionsV2(): NavSection[] {
  const customersChildren: GroupChildNavItem[] = [
    staffChild(
      "Organizations",
      "staff/implementation",
      "Manage organization setup, implementation details, and account context."
    ),
    staffChild(
      "Payments",
      "staff/payments",
      "Review payment records and reconcile report-level transactions."
    ),
    staffChild(
      "Subscriptions",
      "staff/subscription-overview",
      "Compare SFDC contracted subscription tiers and facility counts against Encamp actuals."
    ),
    staffChild(
      "Feature Flags",
      "staff/feature-flags",
      "Manage Encamp-managed feature flags across organizations."
    ),
    staffChild(
      "Roles",
      "staff/roles",
      "Edit the permissions of an organization's roles, and create or remove roles."
    ),
    staffChild("Insights Demo", "staff/insights-demo-org-creator"),
  ];

  const complianceChildren: GroupChildNavItem[] = [
    staffChild(
      "Fulfillment",
      "staff/fulfillment",
      "Monitor report progress, assignments, and workflow outcomes."
    ),
    {
      ...staffChild(
        "Work",
        "staff/work",
        "Every work type in one queue — Tier II reports, cohorts, and waste submissions."
      ),
      previewTitle: "This queue is in development and not yet ready for general use.",
    },
    staffChild(
      "Generator Status",
      "staff/generator-status-changes",
      "Monitor potential generator status discrepancies across facilities."
    ),
    staffChild(
      "State Waste Streams",
      "staff/state-waste-streams",
      "View and edit state waste stream registrations"
    ),
    staffChild(
      "CERS",
      "staff/cers",
      "Review CERS submittal decisions, track CUPA rejections, match scraped CERS facilities to Encamp, and analyze rejection rates."
    ),
    staffChild(
      "Rule Suggestions",
      "staff/rule-suggestions",
      "Review product-detected rule ideas and promote them into Compliance Engine work."
    ),
    staffChild(
      "Violation Match Review",
      "staff/violation-match-review",
      "Release or correct violations matched to a facility through an expired EPA or FRS ID."
    ),
    staffChild("Requirements", "staff/requirements"),
    staffChild(
      "Agencies",
      "staff/agencies",
      "Maintain LEPC, fire department, and state agency records."
    ),
    staffChild(
      "Mail Room",
      "staff/mail-room",
      "Create and track customer communications and delivery status."
    ),
    staffChild(
      "Credentials",
      "staff/credentials",
      "Manage EPCRA, RCRAInfo, and STEERS credential implementations."
    ),
  ];

  const platformChildren: GroupChildNavItem[] = [
    staffChild("Observe", "staff/observe", "Monitor Scout diagnostics and run Scout load tests."),
    staffChild("Jobs", "staff/jobs"),
    staffChild(
      "Notifications",
      "staff/notifications",
      "See every notification sent across organizations, queue new ones, and find the organizations whose alerts reach no one."
    ),
    staffChild("Pipelines", "staff/pipelines"),
    staffChild("Workflow Config", "staff/workflow-config"),
    staffChild("Tags", "staff/tags"),
  ];

  const previewChildren: GroupChildNavItem[] = [
    staffChild(
      "Amazon WADM",
      "staff/amazon-wadm",
      "Search and export Amazon waste generation records by site, profile, and shipment."
    ),
  ];

  return [
    {
      title: "Staff",
      variant: "staff",
      items: [
        {
          kind: "Group",
          text: "Customers",
          description: "Organizations, payments, subscriptions, and demo tooling.",
          Icon: GroupsRoundedIcon,
          path: "staff/implementation",
          isExpandable: true,
          defaultExpanded: true,
          variant: "staff",
          children: customersChildren,
        },
        {
          kind: "Group",
          text: "Compliance",
          description: "Fulfillment, requirements, agencies, mail, and credentials.",
          Icon: HikingIcon,
          path: "staff/fulfillment",
          isExpandable: true,
          defaultExpanded: true,
          variant: "staff",
          children: complianceChildren,
        },
        {
          kind: "Group",
          text: "Platform",
          description: "Observability, jobs, notifications, pipelines, workflow config, and tags.",
          Icon: EngineeringIcon,
          path: "staff/observe",
          isExpandable: true,
          defaultExpanded: true,
          variant: "staff",
          children: platformChildren,
        },
        {
          kind: "Group",
          text: "Preview",
          description: "Experimental staff tools that are still in preview.",
          Icon: ScienceIcon,
          path: "staff/amazon-wadm",
          isExpandable: true,
          defaultExpanded: true,
          variant: "staff",
          children: previewChildren,
        },
      ],
    },
  ];
}
